/** @jsxImportSource @emotion/react */
import APIClient from 'APIClient';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';

import Icon from 'components/Icon';
import Text from 'components/Text';
import handleError from 'utils/handleError';
import { campaignParticipationMiniIconsByName } from 'constants';
import { BLACK, BLUE_FEEK, GRAY, PURPLE_LIGHT } from 'styles/colors';
import { ReactComponent as ZapMini } from 'assets/icons/ZapMini.svg';

const styles = css`
  padding-top: 45px;

  .container {
    padding: 60px 20px 60px 20px;
    max-width: 100%;
    margin: 0 auto;
  }

  .experiences-content {
    padding: 15px 100px;
    text-align: center;
    leading-trim: both;
  }
  b {
    font-weight: 600;
  }

  .experiences-text {
    text-edge: cap;
    font-family: Roobert;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 145%; /* 34.8px */
  }

  .experiences-title {
    font-size: 50px;
  }

  .experiences {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
    margin: 0 auto;
    max-width: 1200px;
    padding: 30px 0px 60px 0px;
  }

  .experience-card {
    border: 2px solid ${PURPLE_LIGHT};
    border-radius: 30px;
    padding: 20px;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 400px;
  }

  .experience-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }

  .experience-card-img {
    width: 100%;
    height: 300px;
    object-fit: cover;
    border-radius: 20px;
    margin-bottom: 15px;
  }

  .place-badge {
    border: 2px solid ${GRAY};
    width: fit-content;
    border-radius: 16px;
    padding: 5px 9px;
    margin-bottom: 15px;
    font-size: 12px;
    font-weight: 600;
    align-self: flex-start;
  }

  .experience-title {
    margin-bottom: 15px;
    flex-grow: 1;
  }

  .flexRow {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .capitalize {
    text-transform: capitalize;
  }

  .mr4 {
    margin-right: 4px;
  }

  .ml12 {
    margin-left: 12px;
  }

  .me-1 {
    margin-right: 0.25rem;
  }

  .mt-3 {
    margin-top: 1rem;
  }

  /* Responsive Design */
  @media (max-width: 1200px) {
    .experiences {
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 20px;
      padding: 30px 0px 60px 0px;
    }

    .experience-card {
      min-height: 380px;
      padding: 18px;
    }

    .experience-card-img {
      height: 250px;
    }
  }

  @media (max-width: 992px) {
    .container {
      padding: 50px 15px 50px 15px;
    }

    .experiences {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 18px;
      padding: 30px 20px 60px 20px;
    }

    .experience-card {
      min-height: 360px;
      padding: 16px;
    }

    .experience-card-img {
      height: 230px;
    }

    /* Make the text column full width on tablets and below */
    .col-7.col-md-7.col-lg-7.offset-1.offset-md-1 {
      flex: 0 0 100%;
      max-width: 100%;
      margin-left: 15px;
      margin-left: 15px;
    }
  }

  @media (max-width: 768px) {
    .container {
      padding: 40px 10px 40px 10px;
    }

    .experiences {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      gap: 16px;
      padding: 30px 20px 60px 20px;
      -webkit-overflow-scrolling: touch;
    }

    .experiences::-webkit-scrollbar {
      display: none;
    }

    .experience-card {
      flex: 0 0 280px;
      min-height: 340px;
      padding: 14px;
      scroll-snap-align: start;
    }

    .experience-card-img {
      height: 200px;
    }

    .experience-title {
      font-size: 16px;
    }
  }

  @media (max-width: 576px) {
    .container {
      padding: 30px 5px 30px 5px;
    }

    .experiences-content {
      padding: 15px 20px;
    }

    .experiences-title {
      font-size: 32px;
    }

    .experiences-text {
      font-size: 18px;
    }

    .experiences {
      gap: 12px;
      padding: 30px 20px 60px 20px;
    }

    .experience-card {
      flex: 0 0 250px;
      min-height: 320px;
      padding: 12px;
    }

    .experience-card-img {
      height: 170px;
    }

    .place-badge {
      font-size: 11px;
      padding: 4px 7px;
    }

    .experience-title {
      font-size: 15px;
    }

    .flexRow {
      font-size: 12px;
    }
  }

  @media (max-width: 768px) {
    .experiences-content {
      padding: 15px 40px;
    }

    .experiences-title {
      font-size: 1.6rem;
    }

    .experiences-text {
      font-size: 20px;
    }
  }

  /* Loading skeleton */
  .skeleton-card {
    border: 2px solid #e0e0e0;
    border-radius: 30px;
    padding: 20px;
    background: #f8f8f8;
    animation: pulse 1.5s ease-in-out infinite;
  }

  .skeleton-img {
    width: 100%;
    height: 300px;
    background: #e0e0e0;
    border-radius: 20px;
    margin-bottom: 15px;
  }

  .skeleton-text {
    height: 16px;
    background: #e0e0e0;
    border-radius: 8px;
    margin-bottom: 10px;
  }

  .skeleton-text.short {
    width: 60%;
  }

  .skeleton-text.long {
    width: 80%;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }
`;

/**
 * ExperienceCard component - displays individual experience/reward cards
 */
const ExperienceCard = ({ experience }) => {
  const formatPlacesText = (places) => {
    return `${places} lugar${places !== 1 ? 'es' : ''}`;
  };

  const formatEndDate = (endDate) => {
    return new Date(endDate).toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="experience-card">
      <img
        src={experience.image}
        alt={experience.title}
        className="experience-card-img"
        loading="lazy"
      />
      <Text
        color={GRAY}
        fontSize={16}
        fontWeight={600}
        className="place-badge"
      >
        {formatPlacesText(experience.places)}
      </Text>

      <Text
        color={BLACK}
        fontSize={18}
        fontWeight={600}
        className="experience-title"
      >
        {experience.title}
      </Text>

      <div className="flexRow">
        <Icon
          size={16}
          className="mr4"
          icon={
            campaignParticipationMiniIconsByName[
              experience.participation
            ]
          }
          color={GRAY}
        />
        <Text
          color={GRAY}
          fontSize={14}
          fontWeight={600}
          className="me-1"
        >
          {experience.participation}
        </Text>
        <Icon
          size={16}
          icon={ZapMini}
          color={GRAY}
          className="mr4 ml12"
        />
        <Text
          color={GRAY}
          fontSize={14}
          fontWeight={600}
          className="capitalize"
        >
          {formatEndDate(experience.end_date)}
        </Text>
      </div>
    </div>
  );
};

const LoadingSkeleton = () => {
  return (
    <div className="experiences">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="skeleton-card">
          <div className="skeleton-img"></div>
          <div className="skeleton-text short"></div>
          <div className="skeleton-text long"></div>
          <div className="skeleton-text long"></div>
          <div className="skeleton-text short"></div>
        </div>
      ))}
    </div>
  );
};

ExperienceCard.propTypes = {
  experience: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    places: PropTypes.number.isRequired,
    participation: PropTypes.string.isRequired,
    end_date: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * FeekExperiences component - displays available experiences/rewards
 * Shows a grid of experience cards with loading and error states
 */
export const FeekExperiences = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const { data } = await APIClient.getExperiencesLanding();
        setExperiences(data.docs.slice(0, 3)); // Limit to 3 experiences
        setLoading(false);
      } catch (err) {
        handleError(err);
        setError('Error al cargar las experiencias');
        setLoading(false);
      }
    };

    fetchExperiences();
  }, []);

  return (
    <div css={styles}>
      <div id="experiences">
        <div className="row w-100 experiences-content">
          <div className="col-12 col-md-12 col-lg-12">
            <Text
              color={BLUE_FEEK}
              fontSize={50}
              className="mb-4 mt-5 experiences-title"
              align="center"
              fontWeight="600"
            >
              ¡Acumula puntos y gana!
            </Text>
            <Text
              color={BLACK}
              fontSize={28}
              fontWeight="300"
              align="center"
              className="experiences-text"
            >
              Cada vez que <b>completas una reserva</b>, ganas
              participaciones que te ayudan a subir de nivel y
              acercarte a{' '}
              <b>
                recompensas increíbles. ¡Entre más reservas hagas, más
                oportunidades tienes de ganar!
              </b>
            </Text>
          </div>
        </div>

        {loading && <LoadingSkeleton />}

        {error && (
          <div className="container">
            <Text color={BLACK} fontSize={24} align="center">
              {error}
            </Text>
          </div>
        )}

        {!loading && !error && (
          <div className="experiences">
            {experiences.map((experience) => (
              <ExperienceCard
                key={experience._id}
                experience={experience}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
